import { z } from 'zod';

export const MAX_NODES = 100;
export const MAX_EDGES = 250;

const id = z.string().trim().min(1).max(64);
const endpoint = z.object({ nodeId: id, port: id });

export const flowNodeSchema = z.object({
  id,
  /** Catalogue key, e.g. `generate.hookVariations`. */
  type: z.string().min(1).max(64),
  typeVersion: z.int().positive(),
  position: z.object({ x: z.number(), y: z.number() }),
  /** Overrides the catalogue label on the canvas. */
  label: z.string().trim().max(80).optional(),
  /** Checked against the node type's own schema by `validateGraph`, not here. */
  config: z.record(z.string(), z.unknown()).default({}),
});

export const flowEdgeSchema = z.object({
  id,
  source: endpoint,
  target: endpoint,
});

/**
 * The stored shape of a flow: nodes and edges keyed by id. This schema only
 * checks structure; whether the graph makes sense is `validateGraph`'s job.
 */
export const flowGraphSchema = z.object({
  schemaVersion: z.literal(1),
  nodes: z
    .array(flowNodeSchema)
    .max(MAX_NODES, `A flow can have up to ${MAX_NODES} nodes`),
  edges: z
    .array(flowEdgeSchema)
    .max(MAX_EDGES, `A flow can have up to ${MAX_EDGES} connections`),
});

export type FlowGraph = z.output<typeof flowGraphSchema>;
export type FlowNode = FlowGraph['nodes'][number];
export type FlowEdge = FlowGraph['edges'][number];
export type Endpoint = FlowEdge['source'];

export function emptyGraph(): FlowGraph {
  return { schemaVersion: 1, nodes: [], edges: [] };
}
